import {
    Box,
    Button,
    CircularProgress,
} from "@mui/material";
import React from "react";
import moment from "moment";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";
import FieldViewService from "../../services/FieldViewService";
import CookAdditionalViewResponse from "../../interfaces/FieldView/CookAdditionalViewResponse";
import FieldViewResponse from "../../interfaces/FieldView/FieldViewResponse";
import AddCookAdditionalDetailValue from "./AddCookAdditionalDetailValue";
import { retrieveFromLocalStorage } from "../../utils/utils";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import ChefForPartyService from "../../services/ChefForPartyService";
import { CustomerService } from "../../services/CustomerService";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

type CookAdditionalDetailsProps = {
    cookId?: number,
    partyId?: number,
    referenceModel?: string
}
type DetailParameterListProps = {
    cookId?: number,
    partyId?: number,
    referenceModel?: string
}
const fieldViewService = new FieldViewService();
const chefForPartyService = new ChefForPartyService();
const customerService = new CustomerService();

const DetailParameterList = (props: DetailParameterListProps) => {
    const [loading, setLoading] = React.useState<boolean>(false);
    const [fieldListData, setFieldListData] = React.useState<FieldViewResponse[]>([]);
    const [label, setLabel] = React.useState<string>('');
    const [inputType, setInputType] = React.useState<string>('');
    const [parameterValues, setParamaterValues] = React.useState<string>('');
    const [openAddValueDialog, setOpenAddValueDialog] = React.useState<boolean>(false);
    const [openUpdateValueDialog, setOpenUpdateValueDialog] = React.useState<boolean>(false);
    const [cookAdditionalDetailsId, setCookAdditionalDetailsId] = React.useState<number>(0);
    const [cookAdditionalData, setCookAdditionalData] = React.useState<CookAdditionalViewResponse[]>([]);
    const [userId, setUserId] = React.useState<number>(0);

    const getAllCookAdditionalDetails = () => {
        setLoading(true);
        const filters = props?.cookId ? {
            cookId: props.cookId,
        } : {
            referenceModelName: props?.referenceModel,
            referenceId: props?.partyId,
        };
        fieldViewService?.getAllCookAdditionalDetails(100, 0, JSON.stringify(filters)).then((res: {total: number, data: CookAdditionalViewResponse[]}) => {
            if (res) {
                setLoading(false);
                setCookAdditionalData(res?.data);
            }
        }).catch(() => {
            setLoading(false);
        });
    };

    const getAllFieldList = () => {
        setLoading(true);
        let filters = {};
        if (props?.referenceModel) {
            filters = { referenceModel: props?.referenceModel };
        }
        fieldViewService?.getAllDetailParameters(100, 0, filters).then((res: {total: number, data: FieldViewResponse[]}) => {
            if (res) {
                setLoading(false);
                console.log(res?.data);
                setFieldListData(res?.data);
            }
        }).catch(() => {
            setLoading(false);
        });
    };

    const handleAddValueClicked = (key: string, inputType: string, parameterValues: string) => {
        setLabel(key);
        setInputType(inputType);
        setParamaterValues(parameterValues);
        setOpenAddValueDialog(true);
    };

    const handleUpdateValueClicked = (key: string, inputType: string, parameterValues: string) => {
        const cookAdditionalDetailId = cookAdditionalData?.find((item: CookAdditionalViewResponse) => item?.key === key)?.id;
        if (cookAdditionalDetailId) {
            setLabel(key);
            setInputType(inputType);
            setParamaterValues(parameterValues);
            setCookAdditionalDetailsId(cookAdditionalDetailId);
            setOpenUpdateValueDialog(true);
        } else {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Add cook detail first.",
                type: CustomSnackbarTypes.ERROR,
            });
        }
    };

    const getUserId = () => {
        const authData = retrieveFromLocalStorage('auth');
        if (authData) {
            const parsedAuthData = JSON.parse(authData);
            if (parsedAuthData && parsedAuthData?.data?.user) {
                setUserId(parsedAuthData?.data?.user?.id);
            }
        }
    };

    const handleAddValuesSubmitClicked = (value: string | string[], key: string) => {
        if (value && key) {
            setLoading(true);
            if (props?.cookId) {
                const payload = {
                    key: key,
                    value: Array.isArray(value) ? JSON.stringify(value) : value,
                    cookId: props.cookId,
                    createdById: userId
                };
                fieldViewService?.createCookAdditionalDetail(payload).then(() => {
                    setLoading(false);
                    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Created Successfully",
                        type: CustomSnackbarTypes.SUCCESS,
                    });
                    setOpenAddValueDialog(false);
                    getAllFieldList();
                    getAllCookAdditionalDetails();
                }).catch(() => { setLoading(false); });
            } else if (props?.partyId) {
                const payload = {
                    key: key,
                    value: Array.isArray(value) ? JSON.stringify(value) : value,
                };
                if (props?.referenceModel !== 'CUSTOMER_MODEL') {
                    chefForPartyService?.updateExtraDetails(props?.partyId, payload).then(() => {
                        setLoading(false);
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                            message: "Created Successfully",
                            type: CustomSnackbarTypes.SUCCESS,
                        });
                        setOpenAddValueDialog(false);
                        getAllFieldList();
                        getAllCookAdditionalDetails();
                    }).catch(() => { setLoading(false); });
                } else {
                    customerService?.updateCustomerExtraDetails(props?.partyId, payload).then(() => {
                        setLoading(false);
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                            message: "Created Successfully",
                            type: CustomSnackbarTypes.SUCCESS,
                        });
                        setOpenAddValueDialog(false);
                        getAllFieldList();
                        getAllCookAdditionalDetails();
                    }).catch(() => { setLoading(false); });
                }
            }
        } else {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Fill all fields",
                type: CustomSnackbarTypes.ERROR,
            });
        }
    };

    const handleUpdateValuesSubmitClicked = (value: string | string[], key: string) => {
        if (value && key && cookAdditionalDetailsId) {
            setLoading(true);
            const payload = {
                value: Array.isArray(value) ? JSON.stringify(value) : value,
            };
            fieldViewService?.updateCookAdditionalDetail(cookAdditionalDetailsId, payload).then(() => {
                setLoading(false);
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Updated Successfully",
                    type: CustomSnackbarTypes.SUCCESS,
                });
                setOpenUpdateValueDialog(false);
                getAllFieldList();
                getAllCookAdditionalDetails();
            }).catch(() => { setLoading(false); });
        } else {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Fill all fields",
                type: CustomSnackbarTypes.ERROR,
            });
        }
    };

    React.useEffect(() => {
        getAllFieldList();
        getAllCookAdditionalDetails();
        getUserId();
        // eslint-disable-next-line
    }, []);

    const columns: CommonListColumn<FieldViewResponse>[] = [
        { label: 'Id', field: 'id' },
        { label: 'Label', field: 'label' },
        {
            label: 'Value',
            render: (field) => {
                const keyExists = cookAdditionalData?.some((item) => item?.key === field?.label);
                if (keyExists) {
                    const item = cookAdditionalData.find((item) => field?.label === item?.key);
                    if (item && item.value) {
                        const isPhoneField = /phone|mobile/i.test(field?.label ?? '');
                        return <span>{isPhoneField ? maskPhoneNumber(item.value) : item.value}</span>;
                    }
                    return <span>null</span>;
                }
                return <span>null</span>;
            },
        },
        {
            label: 'Updated At',
            render: (field) => {
                const item = cookAdditionalData.find((item) => item?.key === field?.label);
                return item?.value && item?.updatedAt
                    ? moment(item.updatedAt).format('DD MMM YYYY hh:mm A')
                    : '-';
            },
        },
        {
            label: '',
            stopRowClick: true,
            render: (field) => {
                const keyExists = cookAdditionalData?.some((item) => item?.key === field?.label);
                const valueExists = !!cookAdditionalData?.find((item) => field?.label === item?.key)?.value;
                if (keyExists && valueExists) return null;
                return (
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        onClick={() => handleAddValueClicked(field?.label, field?.inputType, field?.values)}
                    >
                        Add Value
                    </Button>
                );
            },
        },
        {
            label: '',
            stopRowClick: true,
            render: (field) => {
                const keyExists = cookAdditionalData?.some((item) => item?.key === field?.label);
                const valueExists = !!cookAdditionalData?.find((item) => field?.label === item?.key)?.value;
                if (keyExists && valueExists) {
                    return (
                        <Button
                            variant={'contained'}
                            color={'success'}
                            onClick={() => handleUpdateValueClicked(field?.label, field?.inputType, field?.values)}
                        >
                            Update Value
                        </Button>
                    );
                }
                return null;
            },
        },
    ];

    const activeFields = fieldListData?.filter((each: FieldViewResponse) => each?.isActive) ?? [];

    return (
        <Box>
            {loading ? (
                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'100vh'}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box mt={'30px'}>
                    <CommonListTable
                        columns={columns}
                        data={activeFields}
                        loading={false}
                        rowKey={(row) => row?.id ?? String(row?.label)}
                        emptyText="No Details Found"
                    />
                </Box>
            )}
            {openAddValueDialog && (
                <AddCookAdditionalDetailValue
                    open={openAddValueDialog}
                    onClose={() => setOpenAddValueDialog(false)}
                    label={label}
                    inputType={inputType}
                    parameterValues={parameterValues}
                    submitClicked={handleAddValuesSubmitClicked}
                />
            )}
            {openUpdateValueDialog && (
                <AddCookAdditionalDetailValue
                    open={openUpdateValueDialog}
                    onClose={() => setOpenUpdateValueDialog(false)}
                    label={label}
                    inputType={inputType}
                    parameterValues={parameterValues}
                    submitClicked={handleUpdateValuesSubmitClicked}
                />
            )}
        </Box>
    );
};

const CookAdditionalDetails = (props: CookAdditionalDetailsProps) => {
    return (
        <Box width={'100%'}>
            <DetailParameterList cookId={props?.cookId} partyId={props?.partyId} referenceModel={props?.referenceModel} />
        </Box>
    );
};

export default CookAdditionalDetails;
